import { useMemo } from 'react';
import { Form } from 'react-final-form';
import { Box, Grid, MenuItem } from '@mui/material';

import { TextField, Select } from '@occc-app/design-system';
import { ConfirmDialog, LinearProgress } from '@occc/ui-design-system';
import { Retailer_Network_User_Role, RetailerNetworkLocationDataFragment } from '@occc/graphql';
import { pluralize } from '@occc-app/lib/strings';

import { validateCreateAndAssociateNewUserWithLocations } from './create-and-associate-new-user-with-locations-dialog.validation';
import {
  CreateAndAssociateNewUserWithLocationsFormProps,
  CreateAndAssociateNewUserWithLocationsFormValues,
} from './create-and-associate-new-user-with-locations-dialog.types';

import { RetailerNetworkLocationMultipleSelectField } from '../retailer-network-location-multiple-select-field.component';
import { useRetailerNetworkUserSelector } from '../../retailer-network-context/use-retailer-network-user.selector';

const roleOptions = [
  {
    label: 'Site Admin',
    value: Retailer_Network_User_Role.SiteAdmin,
  },
  {
    label: 'Site User',
    value: Retailer_Network_User_Role.SiteUser,
  },
];

type CreateAndAssociateNewUserWithLocationsDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateAndAssociateNewUserWithLocationsFormValues) => Promise<void>;
  retailerNetworkUuid: string;
  location: RetailerNetworkLocationDataFragment;
};

export const CreateAndAssociateNewUserWithLocationsDialog = ({
  open,
  onClose,
  onSubmit,
  retailerNetworkUuid,
  location,
}: CreateAndAssociateNewUserWithLocationsDialogProps) => {
  const { isRetailerNetworkSiteAdmin, retailerNetworkUserOpenCommerceUserUuid } =
    useRetailerNetworkUserSelector();
  const initialValues = useMemo(() => ({ locations: [location] }), [location]);

  const renderForm: CreateAndAssociateNewUserWithLocationsFormProps['render'] = ({
    handleSubmit,
    submitting,
    valid,
    values,
  }) => {
    const confirmButtonText = `Create user and add to ${pluralize(
      values.locations?.length,
      'location',
    )}`;

    return (
      <ConfirmDialog
        dialogTitle='Add a new user'
        onClose={onClose}
        open={open}
        maxWidth='sm'
        fullWidth
        confirmButtonText={confirmButtonText}
        confirmButtonProps={{
          onClick: handleSubmit,
          disabled: submitting || !valid,
        }}
      >
        <Box pt={1}>
          {submitting && <LinearProgress sx={{ mb: 3 }} />}
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField required name='firstName' label='First Name' />
            </Grid>
            <Grid item xs={6}>
              <TextField required name='lastName' label='Last Name' />
            </Grid>
            <Grid item xs={6}>
              <TextField name='phoneNumber' label='Mobile Phone Number' />
            </Grid>
            <Grid item xs={6}>
              <TextField required type='email' name='email' label='Email' />
            </Grid>
            <Grid item xs={12}>
              <Select required name='userRole' label='Set Role' sx={{ width: '100%' }}>
                {roleOptions
                  .filter((option) =>
                    isRetailerNetworkSiteAdmin
                      ? option.value !== Retailer_Network_User_Role.SiteAdmin
                      : true,
                  )
                  .map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            <Grid item xs={12} mt={-2}>
              <RetailerNetworkLocationMultipleSelectField
                name='locations'
                label='Add user to location'
                retailerNetworkUuidEquals={retailerNetworkUuid}
                openCommerceUserUuidEquals={
                  isRetailerNetworkSiteAdmin ? retailerNetworkUserOpenCommerceUserUuid : undefined
                }
              />
            </Grid>
          </Grid>
        </Box>
      </ConfirmDialog>
    );
  };

  return (
    <Form<CreateAndAssociateNewUserWithLocationsFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      validate={validateCreateAndAssociateNewUserWithLocations}
      initialValues={initialValues}
    />
  );
};
